/**
 * When the Main Event goal is a step that other Frame steps also need content
 * from (today: S2 Items), the Event Designer exposes two facets:
 *   • frame — declare/select content only (no collaborative Go live)
 *   • goal  — tool + execute the collaborative sitting
 */

import { STEP_ORDER, type StepId } from './types';

export type StepFacet = 'frame' | 'goal';

export interface StepNavTarget {
  step: StepId;
  facet: StepFacet;
}

/** Steps that need a Frame content facet when they are also the primary goal. */
export function goalNeedsFrameContentFacet(goalStep: StepId | undefined | null): boolean {
  return goalStep === 'S2';
}

export function sameNavTarget(a: StepNavTarget, b: StepNavTarget): boolean {
  return a.step === b.step && a.facet === b.facet;
}

/**
 * Ordered rail entries for The Frame + Main Event.
 * When goal is S2, Frame includes an Items (pool) entry before S3/S4.
 */
export function buildStepRailEntries(
  requiredSteps: StepId[],
  goalStep: StepId | undefined | null,
): { frame: StepNavTarget[]; mainEvent: StepNavTarget | null } {
  const required = STEP_ORDER.filter((s) => requiredSteps.includes(s));
  const needsFrameS2 = goalNeedsFrameContentFacet(goalStep);

  const frame: StepNavTarget[] = [];
  for (const step of required) {
    if (goalStep && step === goalStep) {
      if (needsFrameS2 && step === 'S2') {
        frame.push({ step: 'S2', facet: 'frame' });
      }
      continue;
    }
    frame.push({ step, facet: 'frame' });
  }

  const mainEvent =
    goalStep && required.includes(goalStep)
      ? ({ step: goalStep, facet: 'goal' } as StepNavTarget)
      : null;

  return { frame, mainEvent };
}

export function defaultNavTarget(
  requiredSteps: StepId[],
  goalStep: StepId | undefined | null,
): StepNavTarget {
  const { frame, mainEvent } = buildStepRailEntries(requiredSteps, goalStep);
  return frame[0] ?? mainEvent ?? { step: 'S1', facet: 'frame' };
}
